import { useCallback, useContext } from "react";

//analytics
import { AnalyticsContext } from "@swiggy-private/checkout-core";

//helpers
import { DO_CLICK_ACTIONS, DO_IMPRESSION_ACTIONS, SCREEN_NAME } from "@dineout-payment-success-ui/analytics/constants";
import { DineoutPaymentSuccessFlowType } from "@dineout-payment-success-ui/interfaces";

interface ICouponCodeClickParams {
    dealId?: string;
    statusTimestamp?: number;
    status?: string;
}

interface IRedemptionDetailsImpressionItem {
    leftItem?: string;
    rightItem?: number;
}

interface IVoucherEntityCardAnalyticsParams {
    title?: string;
    subtitle?: string;
    ctaLink?: string;
    dealId?: string;
}

export const useDinecashStoreAnalytics = (
    isCheckoutFlow?: boolean,
    orderId?: string,
    flowType?: DineoutPaymentSuccessFlowType,
) => {
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);

    const clickHelpBtnAnalytics = useCallback(() => {
        clickEvent({
            category: SCREEN_NAME.DINECASH_PURCHASE_SUCCESS,
            action: DO_CLICK_ACTIONS.HELP,
            context: JSON.stringify({ isCheckoutFlow, flowType }),
            extraParams: JSON.stringify({ orderId }),
        });
    }, [clickEvent, orderId, isCheckoutFlow, flowType]);

    const clickFaqTitle = useCallback(
        (issue?: string, idx?: number) => {
            clickEvent({
                category: SCREEN_NAME.DINECASH_PURCHASE_SUCCESS,
                action: DO_CLICK_ACTIONS.SUCCESS_SCREEN_FAQ,
                label: issue,
                value: String(idx ?? ""),
                context: JSON.stringify({ isCheckoutFlow, flowType }),
                extraParams: JSON.stringify({ orderId }),
            });
        },
        [clickEvent, orderId, isCheckoutFlow, flowType],
    );

    const onHelpImpression = useCallback(() => {
        impressionEvent({
            category: SCREEN_NAME.DINECASH_PURCHASE_SUCCESS,
            action: DO_IMPRESSION_ACTIONS.HELP,
            context: JSON.stringify({ isCheckoutFlow, flowType }),
            extraParams: JSON.stringify({ orderId }),
        });
    }, [impressionEvent, orderId, isCheckoutFlow, flowType]);

    const onItemListImpressionFaq = useCallback(
        (faqId?: number | string, expandable?: boolean) => {
            impressionEvent({
                category: SCREEN_NAME.DINECASH_PURCHASE_SUCCESS,
                action: DO_IMPRESSION_ACTIONS.IMPRESSION_FAQ_ITEM,
                context: JSON.stringify({ faqId, expandable, isCheckoutFlow, flowType }),
                value: String(faqId ?? ""),
                extraParams: JSON.stringify({ orderId }),
            });
        },
        [impressionEvent, orderId, isCheckoutFlow, flowType],
    );

    const onCouponCardImpression = useCallback(
        (dealId?: string) => {
            impressionEvent({
                category: SCREEN_NAME.DINECASH_PURCHASE_SUCCESS,
                action: DO_IMPRESSION_ACTIONS.PURCHASED_COUPON_CARD,
                label: dealId,
                context: JSON.stringify({
                    isCheckoutFlow,
                    flowType,
                    dealId,
                }),
                extraParams: JSON.stringify({ orderId }),
            });
        },
        [impressionEvent, orderId, isCheckoutFlow, flowType],
    );

    const clickCouponCode = useCallback(
        ({ dealId, statusTimestamp, status }: ICouponCodeClickParams) => {
            clickEvent({
                category: SCREEN_NAME.DINECASH_PURCHASE_SUCCESS,
                action: DO_CLICK_ACTIONS.COPY_COUPON_CODE,
                label: dealId,
                context: JSON.stringify({
                    isCheckoutFlow,
                    flowType,
                    statusTimestamp,
                    status,
                }),
                extraParams: JSON.stringify({ orderId }),
            });
        },
        [clickEvent, orderId, isCheckoutFlow, flowType],
    );

    const onRedemptionDetailsImpression = useCallback(
        (items?: IRedemptionDetailsImpressionItem[]) => {
            impressionEvent({
                category: SCREEN_NAME.DINECASH_PURCHASE_SUCCESS,
                action: DO_IMPRESSION_ACTIONS.REDEMPTION_DETAILS,
                context: JSON.stringify({
                    isCheckoutFlow,
                    flowType,
                    items,
                }),
                extraParams: JSON.stringify({ orderId }),
            });
        },
        [impressionEvent, orderId, isCheckoutFlow, flowType],
    );

    const clickVoucherEntityCard = useCallback(
        ({ title, subtitle, ctaLink, dealId }: IVoucherEntityCardAnalyticsParams) => {
            clickEvent({
                category: SCREEN_NAME.DINECASH_PURCHASE_SUCCESS,
                action: DO_CLICK_ACTIONS.VOUCHER_ENTITY_CARD,
                label: title,
                context: JSON.stringify({
                    isCheckoutFlow,
                    flowType,
                    title,
                    subtitle,
                    ctaLink,
                    dealId,
                }),
                extraParams: JSON.stringify({ orderId }),
            });
        },
        [clickEvent, orderId, isCheckoutFlow, flowType],
    );

    const onVoucherEntityCardImpression = useCallback(
        ({ title, subtitle, ctaLink, dealId }: IVoucherEntityCardAnalyticsParams) => {
            impressionEvent({
                category: SCREEN_NAME.DINECASH_PURCHASE_SUCCESS,
                action: DO_IMPRESSION_ACTIONS.VOUCHER_ENTITY_CARD,
                label: title,
                context: JSON.stringify({
                    isCheckoutFlow,
                    flowType,
                    title,
                    subtitle,
                    ctaLink,
                    dealId,
                }),
                extraParams: JSON.stringify({ orderId }),
            });
        },
        [impressionEvent, orderId, isCheckoutFlow, flowType],
    );

    const clickDoneCta = useCallback(() => {
        clickEvent({
            category: SCREEN_NAME.DINECASH_PURCHASE_SUCCESS,
            action: DO_CLICK_ACTIONS.VIEW_ALL_VOUCHERS,
            context: JSON.stringify({ isCheckoutFlow, flowType }),
            extraParams: JSON.stringify({ orderId }),
        });
    }, [clickEvent, orderId, isCheckoutFlow, flowType]);

    const onDoneCtaImpression = useCallback(() => {
        impressionEvent({
            category: SCREEN_NAME.DINECASH_PURCHASE_SUCCESS,
            action: DO_IMPRESSION_ACTIONS.VIEW_ALL_VOUCHERS,
            context: JSON.stringify({ isCheckoutFlow, flowType }),
            extraParams: JSON.stringify({ orderId }),
        });
    }, [impressionEvent, orderId, isCheckoutFlow, flowType]);

    return {
        clickHelpBtnAnalytics,
        clickFaqTitle,
        onHelpImpression,
        onItemListImpressionFaq,
        onCouponCardImpression,
        clickCouponCode,
        onRedemptionDetailsImpression,
        clickVoucherEntityCard,
        onVoucherEntityCardImpression,
        clickDoneCta,
        onDoneCtaImpression,
    };
};
